import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactElement, ReactNode } from 'react';

import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

/** Whole days a date lies in the past; 0 or less means it does not. */
export const daysOverdue = (due?: Date | string | null, now: Date = new Date()): number => {
    if (!due) {
        return 0;
    }

    const day = 24 * 60 * 60 * 1000;
    // Compare calendar days, not instants: a deadline at 08:00 is overdue for
    // the whole day it passed, not from the moment it passed.
    const start = (value: Date): number =>
        Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());

    return Math.floor((start(now) - start(new Date(due))) / day);
};

export interface PaloOverdueBadgeProps {
    /** The date the document was due. */
    due?: Date | string | null;
    /** Only open work can run late — a finished document never does. */
    open: boolean;
    /**
     * "+925 T." instead of "925 Tage überfällig", for the Frist column, which
     * is 100 pixels wide and carries the heading already (GF 12.10.2026).
     */
    short?: boolean;
}

/**
 * How far a piece of open work has run past its date.
 *
 * A list that only prints the deadline leaves the arithmetic to the reader,
 * and an inquiry sat on "Läuft" for days after its end date without anything
 * on the row saying so (#2194). This says it in the row.
 */
export const PaloOverdueBadge = ({ due, open, short }: PaloOverdueBadgeProps): ReactElement | null => {
    const days = open ? daysOverdue(due) : 0;

    if (days <= 0) {
        return null;
    }

    return (
        <PaloDayPill tone="crit">
            <FormattedMessage id={short ? 'app.overdue.daysShort' : 'app.overdue.days'} values={{ days }} />
        </PaloDayPill>
    );
};

const TONES = {
    crit: { color: c.crit, backgroundColor: c.critSoft, borderColor: c.critLine },
    warn: { color: c.warnInk, backgroundColor: c.warnSoft, borderColor: c.warnLine },
    neutral: { color: c.neutralInk, backgroundColor: c.neutralSoft, borderColor: c.line },
};

/** The small pill a row says a number of days with. */
const PaloDayPill = ({ tone, children }: { tone: keyof typeof TONES; children: ReactNode }): ReactElement => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '999px',
            padding: '1px 7px',
            fontSize: '10.5px',
            fontWeight: 640,
            lineHeight: 1.6,
            whiteSpace: 'nowrap',
            border: '1px solid',
            ...TONES[tone],
        }}
    >
        {children}
    </Box>
);

export interface PaloAgeBadgeProps {
    /** Since when the document has been in the state the badge speaks of. */
    since?: Date | string | null;
    /** From this many days on the pill turns amber; below it stays grey. */
    warnFrom: number;
    /** A message taking `days`, e.g. "seit {days} T.". */
    messageId: string;
}

/**
 * How long a document has been sitting in a state that wants someone to move
 * it on — a draft never sent, an inquiry still running (#2260).
 */
export const PaloAgeBadge = ({ since, warnFrom, messageId }: PaloAgeBadgeProps): ReactElement | null => {
    if (!since) {
        return null;
    }

    const days = Math.max(0, daysOverdue(since));

    return (
        <PaloDayPill tone={days >= warnFrom ? 'warn' : 'neutral'}>
            <FormattedMessage id={messageId} values={{ days }} />
        </PaloDayPill>
    );
};
